'use client';

import React from 'react';
import { Card, CardBody } from '@heroui/react';
import { useFormik } from 'formik';
import { createStep1Schema, Step1FormData } from '../validation';
import CustomInput from '../../../../components/input';
import { useTranslations } from '../../../../contexts/translation.context';

interface Step1PersonalDataProps {
  initialValues: Step1FormData;
  onSubmit: (values: Step1FormData) => void;
  onValidationChange: (isValid: boolean) => void;
  forceValidation?: boolean;
}

const Step1PersonalData: React.FC<Step1PersonalDataProps> = ({
  initialValues,
  onSubmit,
  onValidationChange,
  forceValidation = false,
}) => {
  const { t } = useTranslations();
  const step1Schema = React.useMemo(() => createStep1Schema(t), [t]);
  const formik = useFormik({
    initialValues,
    validationSchema: step1Schema,
    onSubmit,
    enableReinitialize: true,
  });

  // Принудительно валидируем форму при forceValidation
  React.useEffect(() => {
    if (forceValidation) {
      console.log('Force validation triggered in Step1');
      formik.validateForm().then(() => {
        formik.setTouched({
          telegram: true,
          contact: true,
        });
      });
    }
  }, [forceValidation]);

  React.useEffect(() => {
    // Форма валидна если поле telegram заполнено (обязательное поле)
    const isValid = formik.isValid && formik.values.telegram.trim() !== '';
    console.log('Step1 validation change:', {
      isValid,
      formikIsValid: formik.isValid,
      telegram: formik.values.telegram,
      errors: formik.errors,
    });
    onValidationChange(isValid);
  }, [formik.isValid, formik.values.telegram, onValidationChange]);

  React.useEffect(() => {
    // Обновляем родительский компонент при изменении данных
    onSubmit(formik.values);
  }, [formik.values, onSubmit]);

  return (
    <Card className="border border-gray-90 rounded-xl shadow-sm">
      <CardBody className="p-4">
        <form onSubmit={formik.handleSubmit}>
          <div className="flex flex-col gap-4">
            <CustomInput
              name="telegram"
              placeholder={t('createAccount.step1.placeholders.telegram')}
              value={formik.values.telegram}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t('createAccount.step1.labels.telegram')}
              variant="bordered"
              labelPlacement="outside"
              required
              className="custom-required-label"
              isInvalid={(formik.touched.telegram || forceValidation) && !!formik.errors.telegram}
              errorMessage={
                formik.touched.telegram || forceValidation ? formik.errors.telegram : undefined
              }
              size="sm"
              classNames={{
                input: 'text-sm text-auth-ink',
                label: 'pb-1 text-sm',
                inputWrapper: 'rounded-lg !h-10',
              }}
            />
            <CustomInput
              name="contact"
              placeholder={t('createAccount.step1.placeholders.contact')}
              //@ts-ignore
              value={formik.values.contact}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              variant="bordered"
              label={t('createAccount.step1.labels.otherContacts')}
              labelPlacement="outside"
              minRows={2}
              isInvalid={(formik.touched.contact || forceValidation) && !!formik.errors.contact}
              errorMessage={
                formik.touched.contact || forceValidation ? formik.errors.contact : undefined
              }
              classNames={{
                input: 'text-sm',
                label: 'pb-2 text-sm',
                inputWrapper: 'rounded-lg min-h-[4.375rem]',
              }}
            />
          </div>
        </form>
      </CardBody>
    </Card>
  );
};

export default Step1PersonalData;
